import Moment from "moment";
import numeral from "numeral";

import {PhoneNumberUtil, PhoneNumberFormat} from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

const filters = {
  title(str) {
    return _(str).words().map(w => _.capitalize(w)).join(' ');
  },
  camelCase(str) {
    return _.camelCase(str);
  },
  concat(str, length) {
    return _.isString(str) ? str.substring(0, length) : str;
  },
  formatOID(str) {
    if (!str) {
      return null;
    }
    if (_.isString(str)) {
      return str;
    }
    if (typeof str === 'object' && str.$oid) {
      return str.$oid;
    }

    return null
  },
  momentDate(date) {
    if (date instanceof Moment) {
      return date;
    } else if (date && date.$date && date.$date.$numberLong) {
      return new Moment(date.$date.$numberLong, 'x')
    } else if (date) {
      return new Moment(date)
    } else {
      return null
    }
  },
  date(date, format) {
    let momentDate = filters.momentDate(date);
    return momentDate !== null ? momentDate.format(format || 'Do MMM YY') : '-';
  },
  dateLong(date, format) {
    let momentDate = filters.momentDate(date);
    return momentDate !== null ? momentDate.format(format || 'Do MMM YYYY') : '-';
  },
  time(date, format) {
    let momentDate = filters.momentDate(date);
    return momentDate !== null ? momentDate.format(format || 'HH:mm') : '-';
  },
  dateTime(date, format) {
    let momentDate = filters.momentDate(date);
    return momentDate !== null ? momentDate.format(format || 'Do MMM YY [at] HH:mm') : '-';
  },
  duration(minutes, type, withoutSuffix) {
    if (_.isNumber(minutes)) {
      return Moment.duration(minutes, type || 'minutes').humanize(withoutSuffix !== true);
    } else {
      return '-';
    }
  },
  durationFromDate(date) {
    let momentDate = filters.momentDate(date);

    if (momentDate instanceof Moment) {
      let diff = momentDate.diff(new Moment(), 'minutes');
      return filters.duration(diff);
    } else {
      return '-';
    }
  },
  getTimeAsDate(time, date) {
    let timeParts = time.split(':');
    date = date || new Moment();

    date.hours(timeParts[0]);
    date.minutes(timeParts[1]);

    return date;
  },
  parsedTelephone(telephone) {
    try {
      let number = phoneUtil.parse(telephone, 'GB');

      if (phoneUtil.isValidNumber(number)) {
        return number;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  },
  telephone(telephone, format) {
    telephone = _.isString(telephone) ? this.parsedTelephone(telephone) : telephone;
    return telephone !== null ? phoneUtil.format(telephone, format || PhoneNumberFormat.NATIONAL) : null;
  },
  telephoneType(telephone, format) {
    telephone = _.isString(telephone) ? this.parsedTelephone(telephone) : telephone;

    if (telephone !== null) {
      return phoneUtil.getNumberType(telephone) === 1 ? 'Mobile' : 'Landline';
    } else {
      return 'Invalid Number';
    }
  },
  number(number, format) {
    format = format === undefined ? '0.[00]a' : format;

    if (number && number.$numberDecimal) {
      number = numeral(number.$numberDecimal)
    } else {
      number = numeral(number);
    }

    if (format === null) {
      const num = parseFloat(number.value());
      // Round to 4 decimal places
      return Math.round(num * 10000) / 10000;
    } else {
      return number.format(format);
    }
  },
  thousand(number) {
    return numeral(number).format('0a')
  },
  currency(number) {
    if (number && number.$numberDecimal) {
      return '£' + numeral(number.$numberDecimal).format('0.[00]a')
    } else {
      return '£' + numeral(number).format('0.[00]a')
    }
  },
  currencyNotReduced(number) {
    if (number == null) {
      return '£0.00';
    }
    if (number && number.$numberDecimal) {
      return '£' + numeral(number.$numberDecimal).format('0,0.00');
    } else {
      return '£' + numeral(number).format('0,0.00');
    }
  },
  currencyPrecise(number) {
    const countDecimals = Math.floor(number) !== number ? number.toString().split(".")[1].length : 0;
    const formatterString = countDecimals <= 2 ? '0,0.00' : countDecimals <= 4 ? '0.' + ('0').repeat(countDecimals) : 4;

    if (number && number.$numberDecimal) {
      return '£' + numeral(number.$numberDecimal).format(formatterString);
    } else {
      return '£' + numeral(number).format(formatterString);
    }
  },
  boolean(boolean) {
    return boolean === true ? 'Yes' : 'No';
  },
  percentage(number, decimalPlaces = 0) {
    let negative = false;

    if (number < 0) {
      negative = true;
      number = number * -1;
    }
    const multiplicator = Math.pow(10, decimalPlaces);

    number = parseFloat((number * multiplicator).toFixed(11));
    number = (Math.round(number) / multiplicator).toFixed(decimalPlaces);

    if (negative) {
      number = (number * -1).toFixed(decimalPlaces);
    }

    return number + '%';
  },
  formatObjectKey(obj, keyPath, format) {
    let value = _.get(obj, keyPath, null);

    if (value !== null && filters[format]) {
      value = filters[format](value);
    }

    return value === null ? '' : value;
  },
  firstXcharacters(string, number) {
    return string.slice(0, number);
  },
  pluralize(string) {
    let plural = {
      '(quiz)$': "$1zes",
      '^(ox)$': "$1en",
      '([m|l])ouse$': "$1ice",
      '(matr|vert|ind)ix|ex$': "$1ices",
      '(x|ch|ss|sh)$': "$1es",
      '([^aeiouy]|qu)y$': "$1ies",
      '(hive)$': "$1s",
      '(?:([^f])fe|([lr])f)$': "$1$2ves",
      '(shea|lea|loa|thie)f$': "$1ves",
      'sis$': "ses",
      '([ti])um$': "$1a",
      '(tomat|potat|ech|her|vet)o$': "$1oes",
      '(bu)s$': "$1ses",
      '(alias)$': "$1es",
      '(octop)us$': "$1i",
      '(ax|test)is$': "$1es",
      '(us)$': "$1es",
      '([^s]+)$': "$1s"
    };

    let array = plural;

    for (var reg in array) {
      let pattern = new RegExp(reg, 'i');

      if (pattern.test(string))
        return string.replace(pattern, array[reg]);
    }

    return string;
  }
};

export default filters;